import React from 'react';
import moment from 'moment';
import Table from '../Table';
import { ITableColumn } from 'src/components/Table/types';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import { displayStatusLabel } from 'src/utils/serviceRequestStatus/displayStatusLabel';

interface ServiceRequestStatsRow {
  propertyName: string;
  suiteNumber: string;
  suiteAddress: string;
  ticketNumber: string;
  submittedDate: string;
  lastUpdated: string;
  status: string;
  customLabel?: string | null;
  daysOpen: number;
}

interface ServiceRequestStatsTableProps {
  reports: ServiceRequestStatsRow[];
}

const sortByDate = (a: string, b: string): number => {
  return moment(a).valueOf() - moment(b).valueOf();
};

const COLUMNS: ITableColumn[] = [
  {
    label: 'Property Name',
    accessor: 'propertyName',
    sortable: true,
    cell: (data: ServiceRequestStatsRow) => data.propertyName,
  },
  {
    label: 'Suite Number',
    accessor: 'suiteNumber',
    sortable: true,
    cell: (data: ServiceRequestStatsRow) => data.suiteNumber,
  },
  {
    label: 'Suite Civic Address',
    accessor: 'suiteAddress',
    sortable: true,
    cell: (data: ServiceRequestStatsRow) => data.suiteAddress,
  },
  {
    label: 'Ticket Number',
    accessor: 'ticketNumber',
    sortable: true,
    cell: (data: ServiceRequestStatsRow) => data.ticketNumber,
  },
  {
    label: 'Submitted Date',
    accessor: 'submittedDate',
    sortable: true,
    sortingAlgorithm: sortByDate,
    cell: (data: ServiceRequestStatsRow) =>
      moment(data.submittedDate).format('DD/MM/YYYY'),
  },
  {
    label: 'Last Updated',
    accessor: 'lastUpdated',
    sortable: true,
    sortingAlgorithm: sortByDate,
    // legacy/workaround: API sends an epoch/min date for never-updated rows; blank those out
    cell: (data: ServiceRequestStatsRow) =>
      moment(data.lastUpdated).year() > 2000
        ? moment(data.lastUpdated).format('DD/MM/YYYY')
        : '',
  },
  {
    label: 'Status',
    accessor: 'status',
    sortable: true,
    cell: (data: ServiceRequestStatsRow) =>
      displayStatusLabel(data.status, data.customLabel),
  },
  {
    label: 'Days Open',
    accessor: 'daysOpen',
    sortable: true,
    cell: (data: ServiceRequestStatsRow) => data.daysOpen,
  },
];

export const ServiceRequestStatsTable = ({
  reports,
}: ServiceRequestStatsTableProps) => {
  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  if (!reports || reports.length === 0) {
    return null;
  }

  return (
    <Table
      columns={COLUMNS}
      // legacy/workaround: Table's data union doesn't include ServiceRequestStatsRow yet
      data={reports as any}
      paginate={{ itemsPerPage, position: 'both' }}
      onItemsPerPageChange={onItemsPerPageChange}
    />
  );
};